/**
 * API Configuration for Static Export
 *
 * This configuration is used when the app is built as a static export.
 * The frontend calls the backend API directly (no Next.js proxy).
 *
 * Environment Variables:
 * - VITE_API_URL: Backend API base URL (required for production)
 * - VITE_CONTROL_PLANE_URL: Control-plane URL for auth redirects (e.g., on 401)
 * - VITE_SSE_ENABLED: Set to 'false' to disable SSE real-time updates
 *
 * Default: http://localhost:8080 (for local development with mock server)
 */

export const API_CONFIG = {
  /**
   * Base URL for all API calls
   * In production, set VITE_API_URL to your backend server URL
   */
  baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:8080',

  /**
   * Control-plane URL for auth redirects (e.g., token refresh on 401).
   * Runtime value injected by the Go SPA handler into window.__APP_CONFIG__
   * (sourced from AGENT_API_BASE_URL). Falls back to VITE_CONTROL_PLANE_URL
   * for local Vite dev where the Go handler isn't in the loop.
   */
  controlPlaneUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.controlPlaneUrl) ||
    import.meta.env.VITE_CONTROL_PLANE_URL ||
    '',

  /**
   * Control-plane workspace id, from window.__APP_CONFIG__. Empty when the
   * compute plane has not yet learned it; callers must treat empty as "silent
   * re-auth unavailable" rather than erroring.
   */
  workspaceId: (typeof window !== 'undefined' && window.__APP_CONFIG__?.workspaceId) || '',

  /**
   * API version prefix
   * All backend endpoints use this prefix
   */
  apiVersion: '/api/v1',

  /**
   * Default request timeout in milliseconds
   */
  timeout: 120000, // 2 minutes

  /**
   * SSE (Server-Sent Events) endpoint for real-time status updates
   */
  sseEndpoint: '/api/v1/events/status',

  /**
   * Base URL for e6data product documentation
   */
  DOC_BASE_URL: 'https://docs.e6data.com/product-documentation/',

  /**
   * Auto-save interval for the query editor (in seconds)
   */
  editorSaveInterval: 30,

  /**
   * How often (ms) the app proactively refreshes the auth session so the
   * shared e6_session cookie never expires under the user — including on the
   * Pipelines remote, which makes its own API calls and can't trigger the
   * host's reactive 401 refresh. Runtime value (injected via
   * window.__APP_CONFIG__) wins so it can be tuned per deployment; falls back
   * to the build-time env var, then a conservative 5-minute default. See
   * useProactiveTokenRefresh.
   */
  sessionRefreshIntervalMs:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.sessionRefreshIntervalMs) ||
    Number(import.meta.env.VITE_SESSION_REFRESH_INTERVAL_MS) ||
    5 * 60 * 1000,

  /**
   * How far back the query-history time filter may reach, in days. Served by
   * compute-api (window.__APP_CONFIG__) so the picker and the API enforce the
   * same number; a hardcoded UI value would drift the moment the deployment
   * tunes QUERY_HISTORY_MAX_WINDOW_DAYS. 0 means no cap.
   */
  queryHistoryMaxWindowDays:
    (typeof window !== 'undefined'
      ? window.__APP_CONFIG__?.queryHistoryMaxWindowDays
      : undefined) ?? 14,
} as const;

/**
 * Build full API endpoint URL
 * @param endpoint - The endpoint path (e.g., '/queryservices')
 * @returns Full URL with base and version prefix
 */
export const getApiUrl = (endpoint: string): string => {
  // If endpoint already starts with /api/v1, don't add prefix
  if (endpoint.startsWith(API_CONFIG.apiVersion)) {
    return `${API_CONFIG.baseUrl}${endpoint}`;
  }
  return `${API_CONFIG.baseUrl}${API_CONFIG.apiVersion}${endpoint}`;
};

export default API_CONFIG;
